import { BackButton, Header, Loader } from "@egovernments/digit-ui-react-components";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import { convertEpochToDateDMY } from "../../../utils";
import Table from "@mui/material/Table";
import TableContainer from "@mui/material/TableContainer";
import TablePagination from "@mui/material/TablePagination";
import TableSortLabel from "@mui/material/TableSortLabel";
import { Box, Button, Checkbox, FormControlLabel, IconButton, InputAdornment, Popover, TextField, Typography } from "@mui/material";
import ClearIcon from "@mui/icons-material/Clear";
import SearchIcon from "@mui/icons-material/Search";
import { ArrowDownward } from "@mui/icons-material";
import FilterListIcon from "@mui/icons-material/FilterList";

const PaymentDirect = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const [data, setData] = useState([]);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [page, setPage] = useState(0);
  const [orderBy, setOrderBy] = useState("applicationNo");
  const [order, setOrder] = useState("asc");
  const [globalSearch, setGlobalSearch] = useState("");
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedColumns, setSelectedColumns] = useState([
    "srNo",
    "name",
    "mobileNumber",
    "applicationNo",
    "applicationType",
    "applicationDate",
    "status",
  ]);
  const [filters, setFilters] = useState({
    name: "",
    mobileNumber: "",
    applicationNo: "",
    applicationType: "",
    status: "",
  });
  const [loader, setLoader] = useState(true); // Loader state

  // Fetch data
  useEffect(() => {
    async function fetchData() {
      setLoader(true); // Start loader
      try {
        const response = await Digit.OBPSService.BPASearchMyApplicationForm12(Digit.ULBService.getCurrentTenantId());
        if (!response || !response.BPA) {
          setData([]);
        } else {
          const filteredData = response.BPA.filter((item) => item.workflow?.action === "MAKE_PAYMENT" && "MAKE_PAYMENT_SELF");
          setData(filteredData);
        }
      } catch (error) {
        setData([]);
        console.error("Error fetching data:", error);
      }
      setLoader(false); // Stop loader
    }
    fetchData();
  }, []);

  // Sorting
  const getComparator = (order, orderBy) => {
    return (a, b) => {
      const getValue = (item) => {
        switch (orderBy) {
          case "name":
            return item?.landInfo?.owners?.[0]?.name?.toLowerCase() || "";
          case "mobileNumber":
            return item?.landInfo?.owners?.[0]?.mobileNumber || "";
          case "applicationNo":
            return item?.applicationNo?.toLowerCase() || "";
          case "applicationType":
            return item?.applicationType?.toLowerCase() || "";
          case "status":
            return t(`WF_BPA_MAP_${item?.status}`)?.toLowerCase() || "";
          case "applicationDate":
            return item?.auditDetails?.createdTime || 0;
          default:
            return "";
        }
      };
      const aValue = getValue(a);
      const bValue = getValue(b);
      if (order === "desc") {
        return bValue > aValue ? 1 : bValue < aValue ? -1 : 0;
      } else {
        return aValue > bValue ? 1 : aValue < bValue ? -1 : 0;
      }
    };
  };

  const handleSort = (column) => {
    const isAsc = orderBy === column && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(column);
  };

  // Filtering
  const matchesSearch = (value, search) => {
    if (!value) return false;
    return value.toString().toLowerCase().includes(search.toLowerCase());
  };

  const getFilteredData = () => {
    let filteredData = data || [];
    if (globalSearch) {
      filteredData = filteredData.filter((item) => {
        return (
          matchesSearch(item?.landInfo?.owners?.[0]?.name, globalSearch) ||
          matchesSearch(item?.landInfo?.owners?.[0]?.mobileNumber, globalSearch) ||
          matchesSearch(item?.applicationNo, globalSearch) ||
          matchesSearch(item?.applicationType, globalSearch) ||
          matchesSearch(item?.status, globalSearch)
        );
      });
    }
    if (filters.name) {
      filteredData = filteredData.filter((item) => item?.landInfo?.owners?.[0]?.name?.toLowerCase().includes(filters.name.toLowerCase()));
    }
    if (filters.mobileNumber) {
      filteredData = filteredData.filter((item) => item?.landInfo?.owners?.[0]?.mobileNumber?.includes(filters.mobileNumber));
    }
    if (filters.applicationNo) {
      filteredData = filteredData.filter((item) => item?.applicationNo?.toLowerCase().includes(filters.applicationNo.toLowerCase()));
    }
    if (filters.applicationType) {
      filteredData = filteredData.filter((item) => item?.applicationType?.toLowerCase().includes(filters.applicationType.toLowerCase()));
    }
    if (filters.status) {
      filteredData = filteredData.filter((item) => t(`WF_BPA_MAP_${item?.status}`)?.toLowerCase().includes(filters.status.toLowerCase()));
    }
    return filteredData;
  };

  const filteredData = getFilteredData();
  const sortedData = filteredData.slice().sort(getComparator(order, orderBy));
  const paginatedData = sortedData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  // Columns
  const allColumns = [
    {
      id: "srNo",
      label: "Sr No",
      filterable: false,
      width: "5%",
      renderCell: (item, index, page, rowsPerPage) => <td className="td-srno">{page * rowsPerPage + index + 1}</td>,
    },
    {
      id: "name",
      label: "Name",
      filterable: true,
      sortable: true,
      width: "12%",
      renderCell: (item) => <td className="td-name">{item?.landInfo?.owners?.[0]?.name || "-"}</td>,
    },
    {
      id: "mobileNumber",
      label: "Mobile No.",
      filterable: true,
      sortable: true,
      width: "10%",
      renderCell: (item) => <td className="td-mobile">{item?.landInfo?.owners?.[0]?.mobileNumber || "-"}</td>,
    },
    {
      id: "applicationNo",
      label: "Application No.",
      filterable: true,
      sortable: true,
      width: "10%",
      renderCell: (item) => (
        <td className="td-appno">
          <strong
            style={{ color: "#007bff", textDecoration: "none", cursor: "pointer" }}
            onClick={() => {
              window.localStorage.setItem("ApplicationStatus", item?.status);
              if (item?.applicationType === "FORM_MAP") {
                history.push({
                  pathname: `/digit-ui/citizen/obps/structuralform12/${item?.applicationNo}`,
                });
              } else if (item?.applicationType === "FORM_LANDUSE") {
                history.push({
                  pathname: `/digit-ui/citizen/obps/structural/formLandUse/${item?.applicationNo}`,
                });
              }
            }}
          >
            {item?.applicationNo || "-"}
          </strong>
        </td>
      ),
    },
    {
      id: "applicationType",
      label: "Application Type",
      filterable: true,
      sortable: true,
      width: "10%",
      renderCell: (item) => <td className="td-apptype">{item?.applicationType || "-"}</td>,
    },
    {
      id: "applicationDate",
      label: "Date",
      filterable: false,
      sortable: true,
      width: "10%",
      renderCell: (item) => <td className="td-date">{convertEpochToDateDMY(item?.auditDetails?.createdTime) || "-"}</td>,
    },
    {
      id: "status",
      label: "Status",
      filterable: true,
      sortable: true,
      width: "13%",
      renderCell: (item) => <td className="td-status">{t(`WF_BPA_MAP_${item?.status}`) || "-"}</td>,
    },
  ];

  const columns = allColumns.filter((col) => selectedColumns.includes(col.id));

  // Column filter popover handlers
  const handleCloseColumnFilter = () => setAnchorEl(null);
  const handleOpenColumnFilter = (event) => setAnchorEl(event.currentTarget);
  const handleColumnToggle = (columnId) => {
    if (selectedColumns.includes(columnId)) {
      if (selectedColumns.length > 1) {
        setSelectedColumns(selectedColumns.filter((id) => id !== columnId));
      }
    } else {
      setSelectedColumns([...selectedColumns, columnId]);
    }
  };

  // Filter change handlers
  const handleFilterChange = (property, value) => {
    setFilters({
      ...filters,
      [property]: value,
    });
    setPage(0);
  };
  const handleClearFilter = (property) => {
    setFilters({
      ...filters,
      [property]: "",
    });
  };

  const handleGlobalSearch = (value) => {
    setGlobalSearch(value);
    setPage(0);
  };

  return (
    <React.Fragment>
      {loader ? (
        <Loader />
      ) : (
        <div style={{ padding: "0px 4px" }}>
          <BackButton />
          <TableContainer className="citizen-table-container">
            <div>
              <caption className="citizen-table-caption">Make Payment - My Applications</caption>
              <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                <Button
                  variant="outlined"
                  startIcon={<FilterListIcon />}
                  className="bg-white"
                  onClick={handleOpenColumnFilter}
                  size="small"
                  sx={{
                    height: "27px",
                    fontSize: "12px",
                    borderColor: "rgb(31, 74, 124)",
                    color: "rgb(31, 74, 124)",
                  }}
                >
                  {t("FASTPAS.GLOBAL.FILTERS.TEXT")}({selectedColumns.length}/{allColumns.length})
                </Button>
                <TextField
                  variant="outlined"
                  size="small"
                  placeholder="Search here..."
                  value={globalSearch}
                  onChange={(e) => handleGlobalSearch(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon sx={{ background: "#1f4a7c", color: "#fff", height: "24px", borderRadius: "7px 0px 0px 7px", width: "29px" }} />
                      </InputAdornment>
                    ),
                    endAdornment: globalSearch && (
                      <InputAdornment position="end">
                        <IconButton size="small" onClick={() => handleGlobalSearch("")}>
                          <ClearIcon fontSize="small" />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    padding: "0px",
                    height: "25px",
                    "& .MuiInputBase-root": {
                      height: "27px",
                      padding: "0px",
                      backgroundColor: '#fff'
                    },
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderColor: "rgb(31, 74, 124)",
                    },
                    "& .MuiOutlinedInput-input": {
                      padding: "0",
                      fontSize: "12px",
                    },
                  }}
                />
              </div>
            </div>
            <Popover
              open={Boolean(anchorEl)}
              anchorEl={anchorEl}
              onClose={handleCloseColumnFilter}
              anchorOrigin={{
                vertical: "bottom",
                horizontal: "left",
              }}
              transformOrigin={{
                vertical: "top",
                horizontal: "left",
              }}
            >
              <div className="select-column-popover">
                <Typography variant="subtitle2" gutterBottom>
                  Select Columns to Display
                </Typography>
                <FormControlLabel
                  control={
                    <Checkbox
                      sx={{ padding: "0px" }}
                      checked={selectedColumns.length === allColumns.length}
                      indeterminate={selectedColumns.length > 0 && selectedColumns.length < allColumns.length}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedColumns(allColumns.map((col) => col.id));
                        } else {
                          setSelectedColumns([allColumns[0].id]);
                        }
                      }}
                    />
                  }
                  sx={{ margin: "0px", fontWeight: "bold" }}
                  label="Select All"
                />
                {allColumns.map((column) => (
                  <div key={column.id}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          sx={{ padding: "0px" }}
                          checked={selectedColumns.includes(column.id)}
                          onChange={() => handleColumnToggle(column.id)}
                          disabled={column.id === "srNo" && selectedColumns.length === 1}
                        />
                      }
                      sx={{ margin: "0px" }}
                      label={column.label}
                    />
                  </div>
                ))}
              </div>
            </Popover>
            <Table stickyHeader aria-label="sticky table" className="citizen-application-table">
              <thead>
                <tr className="citizen-header-row">
                  {columns.map((column) => (
                    <td key={column.id} display="flex" alignitems="center">
                      {column.label}
                      {column.sortable && (
                        <TableSortLabel
                          active={orderBy === column.id}
                          direction={orderBy === column.id ? order : "asc"}
                          onClick={() => handleSort(column.id)}
                          sx={{
                            color: "white !important",
                            "& .MuiTableSortLabel-icon": {
                              opacity: "1 !important",
                              color: "white !important",
                            },
                            "&:hover": {
                              color: "white !important",
                            },
                          }}
                          IconComponent={(props) => (
                            <ArrowDownward
                              {...props}
                              sx={{
                                color: "white !important",
                                opacity: 1,
                              }}
                            />
                          )}
                        />
                      )}
                    </td>
                  ))}
                </tr>
                <tr>
                  {columns.map((column) => (
                    <td key={`filter-${column.id}`}>
                      {column.filterable && (
                        <TextField
                          variant="standard"
                          size="small"
                          value={filters[column.id] || ""}
                          onChange={(e) => handleFilterChange(column.id, e.target.value)}
                          InputProps={{
                            endAdornment: filters[column.id] && (
                              <InputAdornment position="end">
                                <IconButton size="small" onClick={() => handleClearFilter(column.id)}>
                                  <ClearIcon fontSize="small" />
                                </IconButton>
                              </InputAdornment>
                            ),
                          }}
                          sx={{
                            "& .MuiInput-input": {
                              fontSize: "0.75rem",
                            },
                            width: "100%",
                          }}
                        />
                      )}
                    </td>
                  ))}
                </tr>
              </thead>
              <tbody>
                {paginatedData.length === 0 ? (
                  <tr>
                    <td colSpan={columns.length + 5} align="center">
                      No records found
                    </td>
                  </tr>
                ) : (
                  paginatedData.map((item, index) => (
                    <tr hover key={index}>
                      {columns.map((column) => column.renderCell(item, index, page, rowsPerPage))}
                    </tr>
                  ))
                )}
              </tbody>
            </Table>
            <TablePagination
              rowsPerPageOptions={[10, 25, 50, 75, 100]}
              component="div"
              count={filteredData.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={(event, newPage) => setPage(newPage)}
              onRowsPerPageChange={(event) => {
                setRowsPerPage(+event.target.value);
                setPage(0);
              }}
            />
          </TableContainer>
        </div>
      )}
    </React.Fragment>
  );
};

export default PaymentDirect;
